import { useCallback, useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AppContext, type MyPick, type State, type Viewer } from "./state";
import { acknowledgeRule, fetchMe, fetchMyPicks, fetchState, getStubPlayer, setStubPlayer } from "./api";
import { firebaseEnabled, onAuthChanged, signOutUser } from "./firebase";
import { TabBar } from "./components/TabBar";
import { Leaderboard } from "./pages/Leaderboard";
import { Matches } from "./pages/Matches";
import { Results } from "./pages/Results";
import { Profile } from "./pages/Profile";
import { Stats } from "./pages/Stats";
import { Login } from "./pages/Login";
import { TopBar } from "./components/TopBar";
import { RulesUpdateModal } from "./components/RulesUpdateModal";
import { CelebrationModal } from "./components/CelebrationModal";

const INACTIVITY_MS = 60 * 60 * 1000;
const LAST_ACTIVITY_KEY = "wc2026.lastActivity";

export function App() {
  const [state, setState] = useState<State | null>(null);
  const [viewer, setViewerState] = useState<Viewer | null>(null);
  const [myPicks, setMyPicks] = useState<Record<string, MyPick>>({});
  const [authReady, setAuthReady] = useState(!firebaseEnabled()); // stub mode: ready immediately
  const [authError, setAuthError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const me = viewer?.role === "player" ? viewer.player : null;

  const refreshState = useCallback(async () => {
    try {
      const s = await fetchState();
      setState(s);
      setError(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "failed to load state");
    }
  }, []);

  useEffect(() => { refreshState(); }, [refreshState]);

  // In stub mode: hydrate `me` from localStorage when state arrives.
  useEffect(() => {
    if (!state || firebaseEnabled()) return;
    const id = getStubPlayer();
    if (!id) { setViewerState(null); return; }
    void fetchMe().then(setViewerState).catch(() => setViewerState(null));
  }, [state]);

  // In Firebase mode, refresh state after auth resolves so optional
  // viewer-specific visibility rules can return the correct view.
  useEffect(() => {
    if (!firebaseEnabled()) return;
    return onAuthChanged(async (user) => {
      setAuthReady(true);
      setAuthError(null);
      if (!user) {
        localStorage.removeItem(LAST_ACTIVITY_KEY);
        setViewerState(null);
        setMyPicks({});
        await refreshState();
        return;
      }
      // A fresh auth event IS activity — reset the timestamp. (The old code
      // compared against a possibly stale key from before signInWithRedirect's
      // round trip and immediately auto-signed-out fresh sign-ins.) Idle
      // sign-out is still handled by the input-watching effect below.
      localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
      try {
        const identity = await fetchMe();
        if (!identity) {
          setAuthError(`Signed in as ${user.email}, but that email isn't approved for this league. Ask the admin to add you.`);
          setViewerState(null);
        } else {
          setViewerState(identity);
          await refreshState();
        }
      } catch (e: unknown) {
        setAuthError(e instanceof Error ? e.message : "auth failed");
        setViewerState(null);
      }
    });
  }, [refreshState]);

  const setMe = useCallback((id: string | null) => {
    if (id) localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
    else localStorage.removeItem(LAST_ACTIVITY_KEY);
    if (firebaseEnabled()) {
      // In Firebase mode, "logout" means sign out from Firebase.
      if (!id) { signOutUser(); }
    } else {
      setStubPlayer(id);
      if (!id) setMyPicks({});
      if (!state) { setViewerState(null); return; }
      if (!id) { setViewerState(null); return; }
      void fetchMe().then(setViewerState).catch(() => setViewerState(null));
    }
  }, [state]);

  const refreshMyPicks = useCallback(async () => {
    if (viewer?.role !== "player") { setMyPicks({}); return; }
    setMyPicks(await fetchMyPicks());
  }, [viewer]);

  const refreshViewer = useCallback(async () => {
    try {
      const identity = await fetchMe();
      if (identity) setViewerState(identity);
    } catch {
      // Keep the current authenticated view through a transient network error;
      // the next focus/interval check will retry.
    }
  }, []);

  const refresh = useCallback(async () => {
    await Promise.all([refreshState(), refreshMyPicks(), refreshViewer()]);
  }, [refreshState, refreshMyPicks, refreshViewer]);

  // Refresh just after the next open/lock boundary. This makes a long-lived
  // tab reveal picks and advance to the next match without a manual reload.
  useEffect(() => {
    if (!state) return;
    const now = Date.now();
    const nextBoundary = state.matches
      .flatMap((m) => [m.pick_window_open_utc, m.lock_at_utc])
      .map((iso) => new Date(iso).getTime())
      .filter((ms) => Number.isFinite(ms) && ms > now)
      .reduce((a, b) => Math.min(a, b), Infinity);
    if (nextBoundary === Infinity) return;
    const delay = Math.min(nextBoundary - now + 250, 2_147_000_000);
    const timer = window.setTimeout(() => { void refresh(); }, delay);
    return () => window.clearTimeout(timer);
  }, [state, refresh]);

  // Browser timers can be throttled in a background tab; catch up on return.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") void Promise.all([refreshState(), refreshViewer()]);
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [refreshState, refreshViewer]);

  // A long-lived tab should learn about a newly published rule without a hard
  // refresh. This is intentionally lightweight: /api/me returns only identity
  // plus that viewer's pending rule ids, not the full tournament state.
  useEffect(() => {
    if (viewer?.role !== "player") return;
    const timer = window.setInterval(() => { void refreshViewer(); }, 120_000);
    return () => window.clearInterval(timer);
  }, [viewer?.role, refreshViewer]);

  useEffect(() => {
    const onRulesPending = () => { void refreshViewer(); };
    window.addEventListener("wc2026:rules-pending", onRulesPending);
    return () => window.removeEventListener("wc2026:rules-pending", onRulesPending);
  }, [refreshViewer]);

  useEffect(() => { refreshMyPicks(); }, [refreshMyPicks]);

  useEffect(() => {
    if (!viewer) return;
    let timer: ReturnType<typeof setTimeout>;
    const schedule = () => {
      clearTimeout(timer);
      const last = Number(localStorage.getItem(LAST_ACTIVITY_KEY)) || Date.now();
      const remaining = INACTIVITY_MS - (Date.now() - last);
      if (remaining <= 0) { setMe(null); return; }
      timer = setTimeout(() => setMe(null), remaining);
    };
    const markActive = () => {
      localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
      schedule();
    };
    if (!localStorage.getItem(LAST_ACTIVITY_KEY)) markActive();
    else schedule();
    const events = ["pointerdown", "keydown", "scroll", "touchstart"] as const;
    events.forEach((event) => window.addEventListener(event, markActive, { passive: true }));
    return () => {
      clearTimeout(timer);
      events.forEach((event) => window.removeEventListener(event, markActive));
    };
  }, [viewer, setMe]);

  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, behavior: "auto" }); }, [pathname]);

  if (!state) {
    return (
      <div className="loading">
        {error ? <div className="error">{error}</div> : "Loading…"}
      </div>
    );
  }

  if (!authReady) {
    return <div className="loading">Checking sign-in…</div>;
  }

  if (!viewer) {
    return <Login onStubLogin={(id) => setMe(id)} state={state} authError={authError} />;
  }

  const pendingRule = viewer.role === "player" ? viewer.pending_rule_updates[0] : undefined;
  const acknowledgePendingRule = async () => {
    if (!pendingRule) return;
    await acknowledgeRule(pendingRule.id);
    setViewerState(await fetchMe());
  };

  return (
    <AppContext.Provider value={{ state, me, viewerRole: viewer.role, myPicks, setMe, refresh }}>
      <div className="app">
        <TopBar refresh={refresh} />
        <Routes>
          <Route path="/" element={<Leaderboard />} />
          <Route path="/matches" element={<Matches />} />
          <Route path="/results" element={<Results />} />
          <Route path="/stats" element={<Stats />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/profile/:id" element={<Profile />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <TabBar />
        <CelebrationModal leaderboard={state.leaderboard} />
        {pendingRule && <RulesUpdateModal key={pendingRule.id} rule={pendingRule} onAcknowledge={acknowledgePendingRule} />}
      </div>
    </AppContext.Provider>
  );
}
